import PropTypes from 'prop-types'

/**
 * AcademicReferences component - Formats and displays academic references in APA 7th edition format
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.references - References data from API in format { "0": { link, reference }, "1": { link, reference } }
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.numbered - Whether to display as numbered list (default: true)
 * @param {boolean} props.showTitle - Whether to show "References" title (default: true)
 *
 * @returns {JSX.Element} Formatted references list
 */
export const AcademicReferences = ({
  references,
  className = '',
  numbered = true,
  showTitle = true,
}) => {
  // Handle empty or invalid references
  if (!references || typeof references !== 'object') {
    return (
      <div className={`text-gray-400 text-sm italic ${className}`}>No references available</div>
    )
  }

  // Convert references object to array and sort by key
  const referenceEntries = Object.entries(references)
    .sort(([a], [b]) => parseInt(a) - parseInt(b))
    .map(([key, ref]) => ({ id: key, ...ref }))

  // Return early if no references
  if (referenceEntries.length === 0) {
    return (
      <div className={`text-gray-400 text-sm italic ${className}`}>No references available</div>
    )
  }

  /**
   * Formats a single reference according to APA 7th edition standards
   * @param {string} referenceText - Raw reference text from API
   * @param {string} link - DOI or URL link
   * @returns {JSX.Element} Formatted reference with proper typography and clickable DOI
   */
  const formatReference = (referenceText, link) => {
    if (!referenceText) {
      return <span className="text-gray-500 italic">Reference text not available</span>
    }

    // Clean the reference text and remove <scp> tags
    // Also remove any space that follows </scp> to prevent double spaces
    const cleanText = referenceText
      .trim()
      .replace(/<scp>/gi, '')
      .replace(/<\/scp>\s?/gi, '')

    // Try to parse APA format: Author(s). (Year). Title. Journal Name.
    // This is a basic parser - it handles common patterns but may need refinement
    const apaPattern = /^(.+?)\.\s*\((\d{4})\)\.\s*(.+?)\.\s*(.+?)\.?\s*$/
    const match = cleanText.match(apaPattern)

    if (match) {
      const [, authors, year, title, journal] = match

      return (
        <span>
          <span className="font-medium">{authors.trim()}</span>
          <span> ({year}). </span>
          <span>{title.trim()}. </span>
          <span className="italic">{journal.trim()}</span>
          <span>. </span>
          {link && formatDOILink(link)}
        </span>
      )
    }

    // If pattern doesn't match, try to identify journal names for italicization
    // Look for common journal name patterns
    const journalPattern =
      /(.+?)\.\s*([A-Z][A-Za-z\s&]+(?:Journal|Review|Science|Nature|Cell|Proceedings|Research|Studies|Letters|Reports)(?:\s+[A-Za-z\s&]*)?)\./
    const journalMatch = cleanText.match(journalPattern)

    if (journalMatch) {
      const [fullMatch, beforeJournal, journalName] = journalMatch
      const afterJournal = cleanText.slice(fullMatch.length)

      return (
        <span>
          <span>{beforeJournal.trim()}. </span>
          <span className="italic">{journalName.trim()}</span>
          <span>.</span>
          {afterJournal && <span>{afterJournal}</span>}
          {link && (
            <>
              <span> </span>
              {formatDOILink(link)}
            </>
          )}
        </span>
      )
    }

    // Fallback: display as-is with DOI link
    return (
      <span>
        <span>{cleanText}</span>
        {link && (
          <>
            <span> </span>
            {formatDOILink(link)}
          </>
        )}
      </span>
    )
  }

  /**
   * Formats DOI or URL links as styled buttons with proper mobile-responsive display
   * @param {string} link - DOI URL or other link
   * @returns {JSX.Element} Formatted button element
   */
  const formatDOILink = (link) => {
    if (!link) return null

    return (
      <span className="inline-block ml-2" style={{ textIndent: '0' }}>
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-white/90 bg-white/10 backdrop-blur-sm hover:bg-white/20 border border-white/20 hover:border-white/30 rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-1 focus:ring-offset-gray-900/50 whitespace-nowrap min-w-[60px] shadow-sm hover:shadow-md"
          title={`View source: ${link}`}
          style={{ textIndent: '0' }}
        >
          Source
        </a>
      </span>
    )
  }

  const containerClasses = `space-y-3 ${className}`
  const listClasses = numbered ? 'list-decimal list-outside ml-6 space-y-3' : 'space-y-3'
  const itemClasses = 'text-sm leading-relaxed text-gray-200 hanging-indent'

  return (
    <div className={containerClasses}>
      {showTitle && (
        <h3 className="text-lg font-semibold text-white mb-4 border-b border-gray-600 pb-2">
          References
        </h3>
      )}

      {numbered ? (
        <ol className={listClasses}>
          {referenceEntries.map((ref) => (
            <li key={ref.id} className={itemClasses}>
              {formatReference(ref.reference, ref.link)}
            </li>
          ))}
        </ol>
      ) : (
        <div className={listClasses}>
          {referenceEntries.map((ref) => (
            <div key={ref.id} className={itemClasses}>
              {formatReference(ref.reference, ref.link)}
            </div>
          ))}
        </div>
      )}

      {referenceEntries.length > 0 && (
        <div className="text-xs text-gray-500 mt-4 pt-2 border-t border-gray-700">
          {referenceEntries.length} reference{referenceEntries.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  )
}

AcademicReferences.propTypes = {
  references: PropTypes.object.isRequired,
  className: PropTypes.string,
  numbered: PropTypes.bool,
  showTitle: PropTypes.bool,
}

export default AcademicReferences
